import { Grid, makeStyles } from '@material-ui/core'
import HoveredActions from 'components/common/HoveredActions'
import OpenInNewTabIcon from 'components/common/icons/OpenInNewTabIcon'
import ProfilePicture from 'components/common/ProfilePicture'
import Tooltip from 'components/common/Tooltip'
import DazzedParagraph14 from 'components/common/text/DazzedParagraph14'
import useCopilotPatientsList from 'components/CopilotControl/hooks/useCopilotPatientsList'
import PatientStatus from 'components/Patients/PatientStatus'
import { ROUTES } from 'consts'
import moment from 'moment'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { trackEvent } from 'utils/analyticsUtils'
import { getEnvironmentHostname } from 'utils/awsUtils'
import { parseRecentTrackedScans } from '../txTrackingReportLogic'
import TrackedScanIcon from './TrackedScanIcon'

// Figma frame 220 column starts (avatar, Last Scan, Last Scans, Last Reasons) as shares of the row.
const LAYOUT_STYLES = {
  patientInfo: { width: '29.47%' },
  lastScan: { width: '17.24%' },
  lastScans: { width: '22.37%' },
  lastReasons: { width: '30.92%' }
}

// The row shows this many reason chips; the rest collapse into a "+N" chip with a tooltip.
const VISIBLE_REASONS_COUNT = 2

// Figma frame 220 scan icon size and gap. Narrow rows reduce the gap, never the icon size.
const SCAN_ICON_SIZE = 24
const SCAN_ICON_GAP = 30

const useStyles = makeStyles(() => ({
  patientItem: {
    position: 'relative',
    display: 'flex',
    flexWrap: 'nowrap',
    // Figma frame 220 puts the avatar 23px below the header divider; frame 217 rows end 20px below it.
    padding: '23px 23px 20px 28px',
    alignItems: 'center',
    borderBottom: '1px solid #E9E9E9',
    '&:last-child': {
      borderBottom: 'none'
    }
  },
  avatar: {
    maxWidth: 80
  },
  // Figma frame 220 row, measured from the 40px avatar top: name baseline 15px, status baseline 35px,
  // column label baseline 8px, values and chips 17px down. The text blocks keep the avatar's 40px
  // height so they never change the row height.
  patientNameStatusContainer: {
    display: 'flex',
    flexDirection: 'column',
    height: 40,
    marginLeft: 20,
    minWidth: 0,
    flex: 1
  },
  patientName: {
    marginTop: -2.3,
    fontWeight: 500
  },
  patientStatus: {
    marginTop: -4.4,
    marginLeft: -6,
    // PatientStatus renders its text body as the last child of its root, after the status dot.
    '& > div > div:last-child': {
      minWidth: 0
    }
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    height: 40,
    minWidth: 0
  },
  columnLabel: {
    marginTop: -3.2,
    marginBottom: 6.9,
    fontFamily: 'Dazzed',
    fontSize: 13,
    fontWeight: 400,
    lineHeight: 1,
    color: '#727B8C'
  },
  lastScanValue: {
    fontFamily: 'Dazzed',
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '24px',
    color: '#001335'
  },
  scanIcons: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    marginTop: 1.3
  },
  reasons: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    minWidth: 0
  },
  reasonChip: {
    flexShrink: 1,
    minWidth: 0,
    height: 25,
    boxSizing: 'border-box',
    padding: '0 8px',
    borderRadius: 5,
    background: '#E1E5FE',
    fontFamily: 'Dazzed',
    fontSize: 13,
    fontWeight: 500,
    lineHeight: '25px',
    color: '#000000',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis'
  },
  moreReasonsChip: {
    flexShrink: 0,
    cursor: 'default'
  },
  hiddenReasons: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 8
  },
  truncatedText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap'
  }
}))

const TxTrackingPatientListItem = ({ patient, listType, pastXDays, index }) => {
  const classes = useStyles()
  const { t } = useTranslation()
  const { getPatientStatusObject } = useCopilotPatientsList()
  const [isHovered, setIsHovered] = useState(false)

  const {
    id: patientId,
    name: patientName,
    profilePicture,
    statusKey,
    program,
    lastTrackedScanDate,
    lastTrackingReasons,
    recentTrackedScans
  } = patient

  // Stored newest first; shown oldest to newest, left to right, like the Patient Copilot mini timeline.
  const trackedScans = useMemo(() => parseRecentTrackedScans(recentTrackedScans).reverse(), [recentTrackedScans])
  const reasonLabels = useMemo(
    () => (lastTrackingReasons || []).map(reason => t(`dialogs.scanSummary.treatmentTracking.conditions.${reason}`)),
    [lastTrackingReasons, t]
  )
  const visibleReasons = reasonLabels.slice(0, VISIBLE_REASONS_COUNT)
  const hiddenReasons = reasonLabels.slice(VISIBLE_REASONS_COUNT)

  const openPatient = useCallback(
    source => {
      trackEvent('Tx Tracking Report - Patient opened from list', { view: listType, pastXDays, patientId, source })
      window.open(`${getEnvironmentHostname()}${ROUTES.PATIENTS}/${patientId}`, '_blank', 'noopener,noreferrer')
    },
    [listType, pastXDays, patientId]
  )

  const actions = useMemo(
    () => [
      {
        key: 'openPatient',
        icon: <OpenInNewTabIcon />,
        tooltip: t('pages.txTrackingReport.patientsList.actions.openPatient'),
        onClick: () => openPatient('rowAction')
      }
    ],
    [openPatient, t]
  )

  const patientStatus = getPatientStatusObject(statusKey, program)

  return (
    <Grid
      key={index}
      container
      className={classes.patientItem}
      tabIndex={0}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onFocus={() => setIsHovered(true)}
      onBlur={event => !event.currentTarget.contains(event.relatedTarget) && setIsHovered(false)}
    >
      <Grid item style={LAYOUT_STYLES.patientInfo}>
        <Grid container alignItems="center" wrap="nowrap">
          <Grid item className={classes.avatar}>
            <ProfilePicture name={patientName} photo={profilePicture} size="small" />
          </Grid>
          <Grid item className={classes.patientNameStatusContainer}>
            <Grid item>
              <DazzedParagraph14 className={[classes.truncatedText, classes.patientName].join(' ')} title={patientName}>
                {patientName}
              </DazzedParagraph14>
            </Grid>
            <Grid item className={classes.patientStatus}>
              <PatientStatus status={patientStatus} textClassName={classes.truncatedText} showTitle />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      <div className={classes.column} style={LAYOUT_STYLES.lastScan}>
        <span className={classes.columnLabel}>{t('pages.txTrackingReport.patientsList.columns.lastScan')}:</span>
        <span className={classes.lastScanValue}>
          {lastTrackedScanDate ? moment(lastTrackedScanDate).format('MMM D, HH:mm') : '-'}
        </span>
      </div>
      <div className={classes.column} style={LAYOUT_STYLES.lastScans}>
        <span className={classes.columnLabel}>{t('pages.txTrackingReport.patientsList.columns.lastScans')}:</span>
        {/* Preserve Figma's 30px gaps for one to three scans; shrink only the gaps on narrow rows. */}
        <div
          className={classes.scanIcons}
          style={{
            maxWidth: trackedScans.length * SCAN_ICON_SIZE + Math.max(trackedScans.length - 1, 0) * SCAN_ICON_GAP
          }}
        >
          {trackedScans.map(trackedScan => (
            <TrackedScanIcon
              key={trackedScan.grinScanId}
              patientId={patientId}
              trackedScan={trackedScan}
              onTrackReasonsClick={() => openPatient('scanTooltip')}
            />
          ))}
        </div>
      </div>
      <div className={classes.column} style={LAYOUT_STYLES.lastReasons}>
        <span className={classes.columnLabel}>{t('pages.txTrackingReport.patientsList.columns.lastReasons')}:</span>
        <div className={classes.reasons}>
          {visibleReasons.length ? (
            visibleReasons.map(reason => (
              <span key={reason} className={classes.reasonChip} title={reason}>
                {reason}
              </span>
            ))
          ) : (
            <span className={classes.lastScanValue}>-</span>
          )}
          {hiddenReasons.length > 0 && (
            <Tooltip
              placement="bottom"
              variant="light"
              arrow
              value={
                <div className={classes.hiddenReasons}>
                  {hiddenReasons.map(reason => (
                    <span key={reason} className={classes.reasonChip}>
                      {reason}
                    </span>
                  ))}
                </div>
              }
            >
              <span className={[classes.reasonChip, classes.moreReasonsChip].join(' ')}>+{hiddenReasons.length}</span>
            </Tooltip>
          )}
        </div>
      </div>
      <HoveredActions actions={actions} isOpen={isHovered} placement="top" />
    </Grid>
  )
}

export default TxTrackingPatientListItem
